'use client';

import { useCallback, useRef, useState } from 'react';
import Image, { StaticImageData } from 'next/image';
import money from '../../public/stickers/money.png';
import bird from '../../public/stickers/bird.png';
import ufo from '../../public/stickers/ufo.png';
import stairway from '../../public/stickers/stairway.png';
import postit from '../../public/stickers/postit.png';

interface Sticker {
  x: number;
  y: number;
  imageData: StaticImageData;
}

interface MousePosition {
  x: number;
  y: number;
}

const STICKERS: StaticImageData[] = [money, bird, ufo, stairway, postit];
const STICKER_HEIGHT = 100;

const StickerWall = () => {
  const [stickers, setStickers] = useState<Sticker[]>([]);
  const [isMouseInsideStickerWall, setIsMouseInsideStickerWall] =
    useState(false);
  const [currentSticker, setCurrentSticker] = useState(STICKERS[0]);

  const wallRef = useRef<HTMLDivElement>(null);
  const previewStickerRef = useRef<HTMLImageElement>(null);
  const mousePosRef = useRef<MousePosition>({ x: 0, y: 0 });
  const lastUpdateTime = useRef<number>(0);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!wallRef.current || !previewStickerRef.current) return;

    const now = performance.now();
    // Throttle updates to ~60fps (16.67ms)
    if (now - lastUpdateTime.current < 16) return;

    lastUpdateTime.current = now;

    const rect = wallRef.current.getBoundingClientRect();
    const newPos = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    mousePosRef.current = newPos;
    previewStickerRef.current.style.left = `${newPos.x}px`;
    previewStickerRef.current.style.top = `${newPos.y}px`;
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsMouseInsideStickerWall(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsMouseInsideStickerWall(false);
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!wallRef.current) return;

    const rect = wallRef.current.getBoundingClientRect();

    const newSticker: Sticker = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      imageData: currentSticker,
    };

    setStickers((prev) => [...prev, newSticker]);

    const newStickers = STICKERS.filter((s) => s !== currentSticker);
    setCurrentSticker(
      newStickers[Math.floor(Math.random() * newStickers.length)]
    );
  };

  return (
    <div className="w-full h-[80svh] tablet:h-[95svh] bg-black cursor-pointer">
      <div
        ref={wallRef}
        className="w-full h-full select-none relative cursor-pointer flex justify-center items-center"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPointerDown={handlePointerDown}
      >
        <BlankLogo />
        {stickers.map((sticker, index) => (
          <Image
            key={index}
            alt="sticker"
            src={sticker.imageData}
            objectFit="contain"
            priority
            className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
            style={{
              left: sticker.x,
              top: sticker.y,
              height: STICKER_HEIGHT,
              width: 'auto',
            }}
          />
        ))}

        {isMouseInsideStickerWall && (
          <Image
            ref={previewStickerRef}
            alt="Klistremerke"
            src={currentSticker}
            objectFit="contain"
            className="absolute z-5 -translate-x-1/2 -translate-y-1/2 cursor-pointer hidden tablet:block"
            style={{
              left: mousePosRef.current.x,
              top: mousePosRef.current.y,
              height: STICKER_HEIGHT,
              width: 'auto',
            }}
          />
        )}
      </div>
    </div>
  );
};

const BlankLogo = () => {
  return (
    <svg width="96" height="24" viewBox="0 0 96 24">
      <rect width="96" height="24" fill="var(--light)"></rect>
    </svg>
  );
};

export default StickerWall;
